import { clientWithApiClient } from "../../client";
import type { ApiClient } from "../../types";
import type { Response } from "../../types";
import type {
  CreateSoftHoldRequest,
  UpdateSoftHoldRequest,
  SoftHoldResponse,
  SoftHoldStatusResponse,
} from "./types";

/**
 * Creates a new soft hold with Payment Intent
 *
 * This endpoint creates a temporary inventory lock (soft hold) along with a
 * Stripe Payment Intent to secure payment without creating a rental record.
 *
 * The soft hold:
 * - Blocks inventory for 30-90 minutes (based on payment method)
 * - Returns client_secret for Stripe Payment Element
 * - Auto-expires if payment not completed
 * - Returns hold_token for secure updates/cancellation
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the vehicle
 * @param holdData - Soft hold data including vehicle, dates, customer info, and selections
 * @returns Promise that resolves to soft hold response with client_secret and hold_token
 *
 * @example
 * ```typescript
 * const { data } = await softHolds.create(apiClient, "fleet-123", {
 *   vehicle_id: "vehicle-456",
 *   start_date: "2025-09-23",
 *   end_date: "2025-09-26",
 *   customer_email: "john@example.com",
 *   total_amount: "450.00",
 *   metadata: {
 *     selected_addon_ids: ["addon-1", "addon-2"],
 *     selected_insurance_option_ids: ["ins-1"],
 *     adults: 2,
 *     children: 1,
 *     infants: 0,
 *     pets: 0
 *   }
 * });
 * // Returns: { hold_id, hold_token, client_secret, expires_at }
 * ```
 */
const create = (
  apiClient: ApiClient,
  fleetId: string,
  holdData: CreateSoftHoldRequest,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString ? `/soft-holds?${queryString}` : "/soft-holds";

  return clientWithApiClient<Response<SoftHoldResponse>>(apiClient, url, {
    method: "POST",
    body: JSON.stringify(holdData),
  });
};

/**
 * Retrieves the status of a soft hold
 *
 * Used to check if a hold has been successfully converted to a rental
 * after payment completion.
 *
 * @param apiClient - Configured API client instance
 * @param holdId - The soft hold ID
 * @param holdToken - Secure token for verification
 * @returns Promise that resolves to status and optional rental_public_id
 */
const get = (apiClient: ApiClient, holdId: string, holdToken: string) => {
  return clientWithApiClient<Response<SoftHoldStatusResponse>>(
    apiClient,
    `/soft-holds/${holdId}?hold_token=${holdToken}`,
    {
      method: "GET",
    },
  );
};

/**
 * Updates an existing soft hold (e.g., when addons/insurance change)
 *
 * This updates the soft hold metadata and recalculates the Payment Intent amount.
 * Requires the hold_token for security.
 *
 * @param apiClient - Configured API client instance
 * @param holdId - The soft hold ID to update
 * @param holdToken - Secure token from hold creation
 * @param updates - Fields to update (metadata, total_amount)
 * @returns Promise that resolves to updated soft hold response
 *
 * @example
 * ```typescript
 * await softHolds.update(apiClient, "hold-789", "token-abc", {
 *   total_amount: "550.00",
 *   metadata: {
 *     selected_addon_ids: ["addon-1", "addon-2", "addon-3"],
 *     selected_insurance_option_ids: ["ins-1"]
 *   }
 * });
 * ```
 */
const update = (
  apiClient: ApiClient,
  holdId: string,
  holdToken: string,
  updates: UpdateSoftHoldRequest,
) => {
  return clientWithApiClient<Response<SoftHoldResponse>>(
    apiClient,
    `/soft-holds/${holdId}`,
    {
      method: "PATCH",
      body: JSON.stringify({
        hold_token: holdToken,
        ...updates,
      }),
    },
  );
};

/**
 * Cancels a soft hold and its associated Payment Intent
 *
 * This releases the inventory lock and cancels the Stripe Payment Intent.
 * Requires the hold_token for security.
 *
 * @param apiClient - Configured API client instance
 * @param holdId - The soft hold ID to cancel
 * @param holdToken - Secure token from hold creation
 * @returns Promise that resolves when hold is cancelled
 *
 * @example
 * ```typescript
 * await softHolds.cancel(apiClient, "hold-789", "token-abc");
 * ```
 */
const cancel = (apiClient: ApiClient, holdId: string, holdToken: string) => {
  return clientWithApiClient<Response<void>>(
    apiClient,
    `/soft-holds/${holdId}`,
    {
      method: "DELETE",
      body: JSON.stringify({
        hold_token: holdToken,
      }),
    },
  );
};

/**
 * Reads a soft hold with NO API key: the signed `hold_token` alone authorizes
 * it (`GET /v1/public/soft-holds/:id`). For pages a renter reaches from an
 * emailed link, where no fleet API key exists. 401 = bad token, 404 = unknown.
 */
const getPublic = (apiClient: ApiClient, holdId: string, holdToken: string) => {
  return clientWithApiClient<Response<SoftHoldStatusResponse>>(
    apiClient,
    `/public/soft-holds/${encodeURIComponent(holdId)}?hold_token=${encodeURIComponent(holdToken)}`,
    { method: "GET" },
  );
};

/**
 * Cancels a soft hold with NO API key (`DELETE /v1/public/soft-holds/:id`),
 * authorized by the signed `hold_token`. 401 bad token, 404 unknown, 422
 * "Hold is not active" when it can no longer be cancelled.
 */
const cancelPublic = (
  apiClient: ApiClient,
  holdId: string,
  holdToken: string,
) => {
  return clientWithApiClient<Response<void>>(
    apiClient,
    `/public/soft-holds/${encodeURIComponent(holdId)}?hold_token=${encodeURIComponent(holdToken)}`,
    { method: "DELETE" },
  );
};

export const softHolds = {
  create,
  get,
  update,
  cancel,
  getPublic,
  cancelPublic,
};
